/**
 * Applicant SIN helpers: request-link messages, display formatting and request status.
 */

import { toDate } from './showings';

/** "046454286" -> "046 454 286"; anything else is returned unchanged. */
export function formatSin(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 9 ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}` : String(value || '');
}

/** True when the input has exactly 9 digits (spaces and dashes allowed). */
export function isSinInput(value) {
  return /^\d{9}$/.test(String(value || '').replace(/[\s-]/g, ''));
}

function formatDay(value) {
  const d = toDate(value);
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

export function sinRequestMessage({ firstName, link, expiresAt }) {
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  const expiry = formatDay(expiresAt);
  return [
    greeting,
    '',
    'To run the credit check for your rental application, please enter your Social Insurance Number (SIN) using this secure link.' +
      ` It works once${expiry ? ` and expires on ${expiry}` : ''}.`,
    '',
    link,
    '',
    "Please don't send your SIN by email or text message.",
  ].join('\n');
}

export const SIN_REQUEST_SUBJECT = 'Secure form for your SIN - rental application';

export function sinRequestMailto({ email, ...rest }) {
  const params = `subject=${encodeURIComponent(SIN_REQUEST_SUBJECT)}&body=${encodeURIComponent(sinRequestMessage(rest))}`;
  return `mailto:${encodeURIComponent(email || '')}?${params}`;
}

export function sinRequestSms({ phone, ...rest }) {
  const number = String(phone || '').replace(/[^\d+]/g, '');
  return `sms:${number}?body=${encodeURIComponent(sinRequestMessage(rest))}`;
}

/**
 * What to say about a person's SIN on file.
 * @returns {{ tone: 'warning'|'info'|'success'|null, text: string|null }}
 */
export function sinStatus(application, role) {
  const request = application?.sinRequest?.[role];
  if (request?.completedAt) {
    return { tone: 'success', text: `Received through the secure link on ${formatDay(request.completedAt)}` };
  }
  if (application?.sinQuarantine?.[role]) {
    return { tone: 'warning', text: "The SIN on file can't be read. Request it again." };
  }
  if (request?.requestedAt) {
    const expires = toDate(request.expiresAt);
    if (expires && expires < new Date()) {
      return { tone: 'warning', text: `Link sent ${formatDay(request.requestedAt)} has expired` };
    }
    return { tone: 'info', text: `Link sent ${formatDay(request.requestedAt)}, waiting for the applicant` };
  }
  return { tone: null, text: null };
}
